import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { usePersistedState } from "@/lib/persist";
import { fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MailCheck, CalendarCheck, ExternalLink, Search, Link2Off, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDeleteDialog } from "@/components/ConfirmDelete";

export default function Bevestigingen() {
  const [items, setItems] = useState([]);
  const [q, setQ] = usePersistedState("bevestigingen:q", "");
  const [loading, setLoading] = useState(true);
  const [teVerwijderen, setTeVerwijderen] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/inkoop-bevestigingen")
      .then(r => setItems(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const verwijder = async () => {
    if (!teVerwijderen) return;
    try {
      await api.delete(`/inkoop-bevestigingen/${teVerwijderen.id}`);
      setItems(prev => prev.filter(b => b.id !== teVerwijderen.id));
      toast.success("Bevestiging verwijderd");
    } catch {
      toast.error("Verwijderen mislukt");
    } finally {
      setTeVerwijderen(null);
    }
  };

  const filtered = items.filter(b => {
    if (!q) return true;
    const t = q.toLowerCase();
    return (b.inkooporder_nummer || "").toLowerCase().includes(t)
      || (b.van || "").toLowerCase().includes(t)
      || (b.onderwerp || "").toLowerCase().includes(t);
  });

  return (
    <div className="space-y-6" data-testid="bevestigingen-page">
      <div>
        <div className="text-sm text-muted-foreground">Inkoop</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Bevestigingen</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Binnengekomen leveranciersbevestigingen (via e-mail). {filtered.length} bericht(en).
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Zoek op ordernummer, afzender of onderwerp…"
          value={q} onChange={(e) => setQ(e.target.value)} data-testid="bevestiging-search" />
      </div>

      {loading ? (
        <div className="text-muted-foreground">Laden…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-10 text-center text-muted-foreground" data-testid="bevestigingen-leeg">
          <MailCheck className="h-8 w-8 mx-auto mb-3 opacity-40" />
          Nog geen bevestigingen ontvangen. Zodra een leverancier op een inkoopmail reageert, verschijnt die hier.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(b => (
            <div key={b.id} className="bg-card border border-border rounded-xl p-4 space-y-2" data-testid={`bevestiging-${b.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <MailCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                  <span className="font-medium truncate">{b.onderwerp || "(geen onderwerp)"}</span>
                </div>
                <div className="flex items-center gap-2">
                  {b.gekoppeld ? (
                    <Link to={`/inkooporders/${b.inkooporder_id}`} className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                      {b.inkooporder_nummer} <ExternalLink className="h-3 w-3" />
                    </Link>
                  ) : (
                    <Badge variant="secondary" className="text-amber-700 bg-amber-50 border-amber-200">
                      <Link2Off className="h-3 w-3 mr-1" /> Niet gekoppeld
                    </Badge>
                  )}
                  <Button variant="ghost" size="icon"
                    onClick={() => setTeVerwijderen(b)}
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    data-testid={`bevestiging-verwijder-${b.id}`}
                    title="Bevestiging verwijderen">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Van {b.van || "onbekend"} · {fmtDate(b.ontvangen_op)}
              </div>
              {b.herkende_leverdatum && (
                <div className="inline-flex items-center gap-1.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                  <CalendarCheck className="h-4 w-4" /> Herkende leverdatum: {fmtDate(b.herkende_leverdatum)}
                </div>
              )}
              {b.samenvatting && <div className="text-sm text-muted-foreground italic">{b.samenvatting}</div>}
              {b.tekst && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Volledig bericht</summary>
                  <pre className="whitespace-pre-wrap font-sans mt-2 bg-secondary/40 rounded p-3 text-sm">{b.tekst}</pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!teVerwijderen}
        onOpenChange={(o) => !o && setTeVerwijderen(null)}
        title="Bevestiging verwijderen?"
        description={teVerwijderen
          ? `"${teVerwijderen.onderwerp || "(geen onderwerp)"}" van ${teVerwijderen.van || "onbekend"} wordt permanent verwijderd.`
          : ""}
        onConfirm={verwijder}
        testid="bevestiging-delete-dialog"
      />
    </div>
  );
}
